import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext(null);

const ALL_ROLES = ["Administrator", "Owner", "Office Manager", "Employee"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRole] = useState(null);

  const fetchUser = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
      // Sync theme from backend if no local override
      if (res.data.theme_preference) {
        const stored = localStorage.getItem('meridian-theme');
        if (!stored) {
          localStorage.setItem('meridian-theme', res.data.theme_preference);
          document.documentElement.setAttribute('data-theme', res.data.theme_preference);
        }
      }
    } catch {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('token', t);
    setToken(t);
    setUser(u);
    setViewAsRole(null);
    // Apply saved theme preference on login
    if (u.theme_preference) {
      localStorage.setItem('meridian-theme', u.theme_preference);
      document.documentElement.setAttribute('data-theme', u.theme_preference);
    }
    return u;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('meridian-theme');
    setToken(null);
    setUser(null);
    setViewAsRole(null);
    document.documentElement.setAttribute('data-theme', 'dark');
  };

  const isRealAdmin = user?.role === 'Administrator';
  const effectiveRole = (isRealAdmin && viewAsRole) ? viewAsRole : user?.role;

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, logout, setUser,
      effectiveRole, viewAsRole, setViewAsRole, isRealAdmin, ALL_ROLES
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
